import { memo } from "react";

import TableBodyRow from "ds/components/Table/Row";
import TableCell from "ds/components/Table/Row/Cell";
import useRowKeyboardNavigation from "ds/components/Table/useKeyboardNavigation/useRowKeyboardNavigation";
import { TableColumnMeta } from "ds/components/Table/types";
import { TableColumnsState } from "ds/components/Table/useTableStore/types";
import { ManagedUserGroup } from "types/generated";
import { withTestId } from "utils/withTestId";

import { GROUP_IDP_ROW_TEST_ID, IDP_GROUP_COLUMN_ID, ROW_HEADER_ID } from "../../constants";
import { useIdpGroupTableStore } from "../useIdpGroupTableStore";
import { selectVisibleColumns } from "../useIdpGroupTableStore/selectors";
import IdpGroupNameCell from "./NameCell";
import IdpGroupSpacesCell from "./SpacesCell";
import IdpGroupUsersCell from "./UsersCell";
import IdpGroupLastLoginCell from "./LastLoginCell";
import IdpGroupLoginMethodCell from "./LoginMethodCell";
import IdpGroupStatusCell from "./StatusCell";
import IdpGroupDropdownCell from "./DropdownCell";
import IdpGroupDescriptionCell from "./DescriptionCell";

const CELLS = {
  [IDP_GROUP_COLUMN_ID.GROUP_ID]: IdpGroupNameCell,
  [IDP_GROUP_COLUMN_ID.DESCRIPTION]: IdpGroupDescriptionCell,
  [IDP_GROUP_COLUMN_ID.SPACES]: IdpGroupSpacesCell,
  [IDP_GROUP_COLUMN_ID.USERS]: IdpGroupUsersCell,
  [IDP_GROUP_COLUMN_ID.LAST_LOGIN]: IdpGroupLastLoginCell,
  [IDP_GROUP_COLUMN_ID.LOGIN_METHOD]: IdpGroupLoginMethodCell,
  [IDP_GROUP_COLUMN_ID.STATUS]: IdpGroupStatusCell,
  [IDP_GROUP_COLUMN_ID.DROPDOWN]: IdpGroupDropdownCell,
};

type IdpGroupRowProps = {
  group: ManagedUserGroup;
};

type IdpGroupTableState = TableColumnsState<IDP_GROUP_COLUMN_ID>;

const IdpGroupRow = ({ group }: IdpGroupRowProps) => {
  const columns = useIdpGroupTableStore((state: IdpGroupTableState) => selectVisibleColumns(state));
  const keyboardProps = useRowKeyboardNavigation({ isGroup: false });

  return (
    <TableBodyRow {...keyboardProps} {...withTestId(GROUP_IDP_ROW_TEST_ID)}>
      {columns.map(({ id }: TableColumnMeta<IDP_GROUP_COLUMN_ID>) => {
        const columnId = id as IDP_GROUP_COLUMN_ID;
        const Component = CELLS[columnId];
        const uniqueKey = `${columnId}-${group.id}`;
        const isGroupIdColumn = columnId === IDP_GROUP_COLUMN_ID.GROUP_ID;
        const isDropdownColumn = columnId === IDP_GROUP_COLUMN_ID.DROPDOWN;

        return (
          <TableCell
            key={uniqueKey}
            id={id}
            stickyLeft={isGroupIdColumn}
            stickyRight={isDropdownColumn}
            role={columnId === ROW_HEADER_ID ? "rowheader" : undefined}
            align="center"
          >
            <Component group={group} />
          </TableCell>
        );
      })}
    </TableBodyRow>
  );
};

export default memo(IdpGroupRow);
